import React from "react";
import { Table as MantineTable } from "@mantine/core";
import styled, { css } from "styled-components";
export const TABLE_ROW_HOVER_TRANSITION = "background-color 150ms ease-in-out";
export const TABLE_ROW_PINNED_HOVER_BG_VAR = "--table-row-pinned-hover-bg";
export const TABLE_BORDER_WIDTH = 1;
export const flattenTableBackground = (theme, color) => theme.utils.flattenColorStack([theme.colors.global.contentBackground, color]);
export const PINNED_CELL_CLASS_NAME = "table-pinned-cell";
export const PINNED_CELL_STRIPED_CLASS_NAME = "table-pinned-cell-striped";
const STRIPED_ROW_NTH_OF_TYPE = "even";
export const isStripedRowIndex = (index) => STRIPED_ROW_NTH_OF_TYPE === "even" ? index % 2 === 0 : index % 2 !== 0;
export const getPinnedCellClassName = (isPinned, isStripedRow) => {
  if (!isPinned) {
    return void 0;
  }
  return isStripedRow ? PINNED_CELL_STRIPED_CLASS_NAME : PINNED_CELL_CLASS_NAME;
};
const StyledTable = styled(MantineTable)(
  ({ theme, $striped, $hover, $condensed, $bordered }) => css`
    --table-border-color: ${theme.colors.table.row.divider};
    font-size: inherit;
    line-height: inherit;
    border-collapse: separate;
    border-spacing: 0;

    ${$bordered && css`
      border-top: ${TABLE_BORDER_WIDTH}px solid ${theme.colors.table.row.divider};
      border-left: ${TABLE_BORDER_WIDTH}px solid ${theme.colors.table.row.divider};
    `}

    & th,
    & td {
      padding: ${$condensed ? "5px" : "8px"};
      vertical-align: middle;
      border-top: ${TABLE_BORDER_WIDTH}px solid ${theme.colors.table.row.divider};
      ${$bordered && css`
        border-top: none;
        border-bottom: ${TABLE_BORDER_WIDTH}px solid ${theme.colors.table.row.divider};
      `}
    }

    ${$bordered && css`
      & th {
        border-right: ${TABLE_BORDER_WIDTH}px solid ${theme.colors.table.row.divider};
      }

      & td {
        border-right: ${TABLE_BORDER_WIDTH}px solid ${theme.colors.table.column.divider};
      }

      & tr > td:last-child {
        border-right: ${TABLE_BORDER_WIDTH}px solid ${theme.colors.table.row.divider};
      }
    `}

    & thead > tr > th {
      background-color: ${theme.colors.table.head.background};
      white-space: nowrap;
      vertical-align: middle;
      border-top: none;
      border-bottom: ${TABLE_BORDER_WIDTH}px solid ${theme.colors.table.row.divider};
      font-weight: normal;
    }

    & thead + tbody > tr:first-child > td {
      border-top: none;
    }

    & tbody > tr {
      background-color: ${theme.colors.global.contentBackground};
      transition: ${TABLE_ROW_HOVER_TRANSITION};
    }

    ${$striped && css`
      & tbody:only-of-type > tr:nth-of-type(${STRIPED_ROW_NTH_OF_TYPE}) {
        background-color: ${theme.colors.table.row.backgroundStriped};
      }

      & tbody:not(:only-of-type):nth-of-type(odd) > tr {
        background-color: ${theme.colors.table.row.background};
      }

      & tbody:not(:only-of-type):nth-of-type(even) > tr {
        background-color: ${theme.colors.table.row.backgroundStriped};
      }
    `}

    ${$hover && css`
      & tbody > tr:hover {
        background-color: ${theme.colors.table.row.backgroundHover};
        ${TABLE_ROW_PINNED_HOVER_BG_VAR}: ${flattenTableBackground(theme, theme.colors.table.row.backgroundHover)};
      }

      & tbody > tr:hover > .${PINNED_CELL_CLASS_NAME}, & tbody > tr:hover > .${PINNED_CELL_STRIPED_CLASS_NAME} {
        background-color: var(${TABLE_ROW_PINNED_HOVER_BG_VAR});
      }
    `}

    ${$striped && $hover && css`
      & tbody:only-of-type > tr:hover,
      & tbody:not(:only-of-type) > tr:hover {
        background-color: ${theme.colors.table.row.backgroundHover};
        ${TABLE_ROW_PINNED_HOVER_BG_VAR}: ${flattenTableBackground(theme, theme.colors.table.row.backgroundHover)};
      }
    `}

    & thead > tr > th.${PINNED_CELL_CLASS_NAME} {
      background-color: ${flattenTableBackground(theme, theme.colors.table.head.background)};
    }

    & tbody > tr > .${PINNED_CELL_CLASS_NAME}, & tfoot > tr > .${PINNED_CELL_CLASS_NAME} {
      background-color: ${flattenTableBackground(theme, theme.colors.table.row.background)};
      transition: ${TABLE_ROW_HOVER_TRANSITION};
    }

    & tbody > tr > .${PINNED_CELL_STRIPED_CLASS_NAME} {
      background-color: ${flattenTableBackground(theme, theme.colors.table.row.backgroundStriped)};
      transition: ${TABLE_ROW_HOVER_TRANSITION};
    }

    @media print {
      & thead > tr > th {
        white-space: break-spaces;
        word-break: break-all;
      }
    }
  `
);
const Table = ({
  children = void 0,
  className = void 0,
  "data-testid": dataTestId = void 0,
  striped = true,
  hover = true,
  condensed = void 0,
  bordered = void 0,
  responsive = void 0
}) => {
  const table = /* @__PURE__ */ React.createElement(
    StyledTable,
    {
      className,
      "data-testid": dataTestId,
      $striped: striped,
      $hover: hover,
      $condensed: condensed,
      $bordered: bordered
    },
    children
  );
  if (responsive) {
    return /* @__PURE__ */ React.createElement("div", { style: { overflowX: "auto" } }, table);
  }
  return table;
};
export default Table;
